import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Positions.styles.scss";

// Type Imports
import { NormalizedPositionsRow } from "./Positions.types";
import { IAlertMessage } from "@pulse-ui/types/components/AlertMessage";

// Component Imports
import { AlertMessage } from "../../components";
import { DropdownInput } from "../../components/Inputs";
import { Loading } from "../../layout";
import SocketAGTable from "../../components/SocketAGTable/SocketAGTable";
import VenueCell from "../../components/AGCellRenderers/VenueCell";

// Material UI Imports
import { SvgIcon } from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

// Package Imports
import { v4 as uuidv4 } from "uuid";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";

// Formatting Functions
import { grab_base_url } from "../../utils/api_methods";
import {
  currencyFormatter,
  customNumericSortFunc,
  findAccountWallet,
  findPartyExchange,
  numericFormatterNoPrecision,
} from "../../utils/helper_functions";

// Context
import { useAppContext } from "../../context/context";

// Type Imports
import {
  ITradingPositionReport,
  ITradingPositionRestatement,
} from "@pulse-ui/codegen/generated_ts/position_management";
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";
import { ContextStateType } from "../../context/context.types";
import {
  IAccountMetadata,
  IPortfolioMetadata,
} from "@pulse-ui/codegen/generated_ts/org_metadata";
import { IAGPositionsSocketDataRow } from "@pulse-ui/types/components/AGTable";

// SVG Imports
import LogoSVG from "@/svgs/pulse_prime_logo.svg?react";

// Promise Function Imports
import {
  getOrgMetadata,
  getOrgs,
  getPricingAssets,
  setAccountById,
  setOrgMetadata,
  setPortfolioById,
} from "../../utils/promises";

// Hooks
import { usePortfolioAndAccountSelection } from "../../utils/hooks/usePortfolioAndAccountSelection/usePortfolioAndAccountSelection";
import { useAlert } from "@/utils/hooks/useAlert/useAlert";

const Positions = () => {
  // State - START -------------------------------------
  const ALL_ACCOUNTS = "all-accounts";
  const PERP = "PERP";
  const LIST = "list";
  const NOTIONAL = "notional";

  // Query Params States
  const [queryParams, setQueryParams] = useSearchParams({
    accounts: "",
    view: "list",
    notional_asset: "USDT",
  });
  const location_origin = window.location.origin;
  const accounts_param = queryParams.get("accounts");
  const view_param = queryParams.get("view");
  const notional_asset_param = queryParams.get("notional_asset");
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [readyForSocket, setReadyForSocket] = useState<boolean>(false);

  // Error Handling / Alert Message States
  const { alerts, pushAlert, closeAlert, clearAlerts } = useAlert();

  // Context States
  const {
    context_state,
    update_org_metadata,
    set_portfolio_by_id,
    set_account_by_id,
  } = useAppContext();

  // Portfolio/Account/PricingAssets Selection States
  const stringified_local_metadata: string | null =
    localStorage.getItem("pulse-ctx");
  const local_metadata: ContextStateType | null = stringified_local_metadata
    ? JSON.parse(stringified_local_metadata)
    : null;

  const [dataMap, setDataMap] = useState<Map<string, any>>(new Map([]));
  const socketRef = useRef<WebSocket | null>(null);
  const dataMapRef = useRef(dataMap);
  const gridRef = useRef<AgGridReact<IAGPositionsSocketDataRow> | null>(null);
  const [initialRender, setInitialRender] = useState<boolean>(true);
  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [pricingAssets, setPricingAssets] = useState<ISelectOption[]>([]);
  // State - END ---------------------------------------

  // Methods - START -----------------------------------
  const get_query_endpoint = (view: typeof LIST | typeof NOTIONAL) => {
    switch (view) {
      case "list":
        return `/api/dropcopy/v1/position_update?accounts=`;
      case "notional":
        return `/api/portfolio/v1/position?accounts=`;
      default:
        "";
    }
  };

  const disconnectFromWebsocket = () => {
    if (socketRef.current) {
      if (
        socketRef.current.readyState === 0 ||
        socketRef.current.readyState === 1 ||
        socketRef.current.readyState === 3
      ) {
        socketRef.current.close();
        socketRef.current = null;
      }
    }
  };

  const handleNavigate = (
    account_id: string,
    view: string,
    notional_asset: string | null
  ) => {
    setDataMapRef(new Map([]));
    setQueryParams((prev: URLSearchParams) => {
      const current_notional_asset_param = queryParams.get("notional_asset");

      prev.set("accounts", account_id);
      prev.set("view", view);

      if (!notional_asset && current_notional_asset_param) {
        prev.delete("notional_asset");
      } else {
        prev.set("notional_asset", notional_asset || "USDT");
      }

      return prev;
    });
  };

  const mapIPortfoliosToSelectOptions = (
    portfolios: IPortfolioMetadata[]
  ): ISelectOption[] => {
    if (!portfolios) {
      return [];
    } else {
      let options: ISelectOption[] = [];

      for (let portfolio of portfolios) {
        options.push({
          label: portfolio.display_name,
          value: portfolio.portfolio_id,
        });
      }

      return options;
    }
  };

  const mapIAccountsToSelectOptions = (
    accounts: IAccountMetadata[]
  ): ISelectOption[] => {
    if (!accounts) {
      return [];
    } else {
      let options: ISelectOption[] = [];

      for (let account of accounts) {
        options.push({
          label: account.display_name,
          value: account.account_id,
        });
      }

      options.unshift({
        label: "All Accounts",
        value: ALL_ACCOUNTS,
      });

      return options;
    }
  };

  const mapPricingAssetsToSelectOptions = (
    pricing_assets: string[]
  ): ISelectOption[] => {
    if (!pricing_assets) {
      return [];
    } else {
      let options: ISelectOption[] = [];

      for (let asset of pricing_assets) {
        options.push({
          label: asset,
          value: asset,
        });
      }

      return options;
    }
  };

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    disconnectFromWebsocket();

    if (alerts.length > 0) clearAlerts();

    if (name === "portfolio-select") {
      setDataMapRef(new Map([]));

      value === "" ? set_portfolio_by_id(null) : set_portfolio_by_id(value);
      set_account_by_id(null);
      return;
    }

    if (name === "account-select") {
      value === "" ? set_account_by_id(null) : set_account_by_id(value);
    }

    if (name === "notional-asset-select") {
      setQueryParams((prev: URLSearchParams) => {
        prev.set("notional_asset", value);
        return prev;
      });

      handleNotionalAssetChange(value);
    }
  };

  const handleInitialDataLoad = async () => {
    setLoading(true);

    try {
      const get_orgs_res = await getOrgs(location_origin);
      const get_org_metadata_res = await getOrgMetadata(
        get_orgs_res.data.org_id,
        location_origin
      );
      await setOrgMetadata(get_org_metadata_res.data, update_org_metadata);

      const get_pricing_assets_res = await getPricingAssets(location_origin);
      setPricingAssets(
        mapPricingAssetsToSelectOptions(get_pricing_assets_res.data)
      );

      const { portfolio_id_res, account_id_res } =
        await usePortfolioAndAccountSelection(
          accounts_param,
          local_metadata,
          get_org_metadata_res.data,
          true
        );

      const portfolio_by_portfolio_id_res =
        get_org_metadata_res.data.portfolios.filter(
          (portfolio: IPortfolioMetadata) =>
            portfolio.portfolio_id == portfolio_id_res
        );

      handleNavigate(
        account_id_res || "",
        view_param || LIST,
        notional_asset_param || null
      );

      await setPortfolioById(portfolio_id_res || null, set_portfolio_by_id);
      await setAccountById(account_id_res, set_account_by_id);

      let accounts: string | null = null;
      if (account_id_res === ALL_ACCOUNTS) {
        accounts =
          (portfolio_by_portfolio_id_res[0] &&
            portfolio_by_portfolio_id_res[0].accounts &&
            portfolio_by_portfolio_id_res[0].accounts
              .map((account: any) => account.account_id)
              .join(",")) ||
          null;

        setColDefs(
          getColDefs(
            view_param === LIST
              ? colDefKeys.all_accounts_list
              : colDefKeys.all_accounts_notional
          )
        );
      } else {
        accounts = account_id_res;

        setColDefs(
          getColDefs(
            view_param === LIST ? colDefKeys.list : colDefKeys.notional
          )
        );
      }

      if (portfolio_id_res) {
        if (accounts) {
          setReadyForSocket(true);
        }
      } else {
        set_account_by_id(null);
        set_portfolio_by_id(null);
        navigate("/unauthorized", { replace: true });
      }
    } catch (error_res: any) {
      if (error_res.status === 408) {
        pushAlert({
          id: uuidv4(),
          message: error_res.message,
          status: 408,
          onClickText: "Reload page?",
          onClickMethod: () => window.location.reload(),
        });
      } else {
        pushAlert({
          id: uuidv4(),
          message: error_res.message,
          status: error_res?.status || 500,
        });
      }
    } finally {
      if (tableLoading) setTableLoading(false);
      setLoading(false);
    }
  };

  const setDataMapRef = (newDataMap: Map<any, any>) => {
    dataMapRef.current = newDataMap;
    setDataMap(newDataMap);
  };

  const isTradingPositionRestatement = (
    row: ITradingPositionRestatement | ITradingPositionReport
  ): row is ITradingPositionRestatement => {
    return (
      (row as ITradingPositionRestatement).trading_positions_snapshot !==
      undefined
    );
  };

  const parsePositionsReport = (
    rpt: ITradingPositionReport,
    account_details: IAccountMetadata[]
  ): {
    filtered_row_count: number;
    map: Map<string, NormalizedPositionsRow>;
  } => {
    const {
      rpt_id,
      clearing_date,
      parties,
      account,
      symbol,
      contract_size,
      notional_asset,
      notional_short,
      notional_long,
      pos_qtys,
      pos_event_type,
      updated,
    } = rpt;

    let parsed_positions_rpt_map: Map<string, NormalizedPositionsRow> =
      new Map();
    let filtered_row_count = 0;

    if (pos_qtys.length > 0) {
      for (let qty of pos_qtys) {
        const { pos_type, long_qty, short_qty, unrealized_pnl, mark_price } =
          qty;
        const shouldKeep =
          Math.abs(qty.long_qty || 0) > 0 ||
          Math.abs(qty.short_qty || 0) > 0 ||
          Math.abs(qty.unrealized_pnl || 0) > 0;

        const rowId: string = `${findPartyExchange(parties)?.party_id}${
          findAccountWallet(parties)?.party_id || ""
        }.${PERP}.${symbol.toString()}-${account}`;

        if (!shouldKeep) {
          filtered_row_count++;
        } else {
          parsed_positions_rpt_map.set(rowId, {
            row_id: rowId,
            rpt_id: rpt_id || "-",
            clearing_date: clearing_date || "-",
            venue: findPartyExchange(parties)?.party_id || "-",
            account:
              account_details.filter(
                (acct: any) => acct.account_id === account
              )[0]?.display_name || "-",
            wallet: findAccountWallet(parties)?.party_id || "-",
            symbol: symbol.toString() || "-",
            contract_size: contract_size?.toString() || "-",
            notional_asset: notional_asset || "-",
            notional_long: notional_long?.toString() || "-",
            notional_short: notional_short?.toString() || "-",
            pos_event_type: pos_event_type.toString() || "-",
            updated: updated,
            pos_type: pos_type.toString() || "-",
            long_qty: long_qty.toString() || "-",
            short_qty: short_qty.toString() || "-",
            list_unrealized_pnl: "-",
            notional_unrealized_pnl: unrealized_pnl?.toString() || "-",
            mark_price: mark_price?.toString() || "-",
          });
        }
      }
    }

    return {
      filtered_row_count,
      map: parsed_positions_rpt_map,
    };
  };

  const parsePositionsRestatement = (
    restatement: ITradingPositionRestatement,
    account_details: IAccountMetadata[]
  ): {
    filtered_row_count: number;
    map: Map<string, NormalizedPositionsRow>;
  } => {
    const { trading_positions_snapshot } = restatement;

    let filtered_row_count = 0;
    let map: Map<string, NormalizedPositionsRow> = new Map();

    if (trading_positions_snapshot.length > 0) {
      for (let rpt of trading_positions_snapshot) {
        const parsed_positions_rpt = parsePositionsReport(rpt, account_details);

        if (parsed_positions_rpt.map && parsed_positions_rpt.map.size > 0) {
          for (const [key, value] of parsed_positions_rpt.map) {
            map.set(key, value);
          }

          filtered_row_count += parsed_positions_rpt.filtered_row_count;
        }
      }
    }

    return {
      filtered_row_count,
      map,
    };
  };

  const connectToWebsocket = (
    accounts: string,
    accounts_details: IAccountMetadata[] | null,
    endpoint: string
  ): Promise<{
    status: number;
    error: any;
  }> => {
    return new Promise((resolve, reject) => {
      const jwtToken = localStorage.getItem("pulsejwt");

      const websocket_url = `${grab_base_url(
        location_origin,
        "wss"
      )}${endpoint}`;

      if (
        jwtToken &&
        (socketRef.current === null ||
          !socketRef.current.url.includes(accounts)) &&
        accounts_details
      ) {
        socketRef.current = new WebSocket(websocket_url, [jwtToken]);

        let messageReceived = false;
        let messageTimeout: any;

        const connectionTimeout = setTimeout(() => {
          socketRef.current?.close();
          socketRef.current = null;
          reject({
            status: 408,
            data: null,
            error: "Connection timed out",
          });
        }, 5000);

        socketRef.current.onopen = () => {
          clearTimeout(connectionTimeout);

          messageTimeout = setTimeout(() => {
            if (!messageReceived) {
              socketRef.current?.close();
              socketRef.current = null;
              reject({
                status: 408,
                data: null,
                error: "No messages received",
              });
            }
          }, 5000);
        };

        socketRef.current.onmessage = (event: any): void => {
          const parsedEvent:
            | ITradingPositionRestatement
            | ITradingPositionReport = JSON.parse(event.data);

          messageReceived = true;
          clearTimeout(messageTimeout);

          if (isTradingPositionRestatement(parsedEvent)) {
            const new_trading_positions_snapshot: ITradingPositionReport[] =
              parsedEvent.trading_positions_snapshot;

            if (new_trading_positions_snapshot.length > 0) {
              const parsed_positions_restatements_res: {
                filtered_row_count: number;
                map: Map<string, NormalizedPositionsRow> | null;
              } = parsePositionsRestatement(parsedEvent, accounts_details);

              if (
                parsed_positions_restatements_res &&
                parsed_positions_restatements_res.map
              ) {
                const mergedMap = new Map<any, any>(dataMapRef.current);

                for (const [
                  key,
                  value,
                ] of parsed_positions_restatements_res.map) {
                  mergedMap.set(key, value);
                }

                const mapEntries = [...mergedMap.entries()];

                mapEntries.sort((a, b) => {
                  const a_symbol = a[1].symbol || "";
                  const b_symbol = b[1].symbol || "";

                  return a_symbol.localeCompare(b_symbol);
                });

                const sortedMap = new Map(mapEntries);

                setDataMapRef(sortedMap);

                resolve({
                  status: 200,
                  error: null,
                });
              }
            } else {
              resolve({
                status: 200,
                error: null,
              });
            }
          } else {
            if (parsedEvent.pos_qtys.length > 0) {
              const parsed_positions_rpt_res = parsePositionsReport(
                parsedEvent,
                accounts_details
              );

              const mergedMap = new Map<any, any>(dataMapRef.current);

              for (const [key, value] of parsed_positions_rpt_res.map) {
                mergedMap.set(key, value);
              }

              const mapEntries = [...mergedMap.entries()];

              mapEntries.sort((a, b) => {
                const a_asset_code = a[1].asset || "";
                const b_asset_code = b[1].asset || "";

                return a_asset_code.localeCompare(b_asset_code);
              });

              const sortedMap = new Map(mapEntries);

              setDataMapRef(sortedMap);
            }
          }
        };

        socketRef.current.onerror = (event: any) => {
          reject({
            status: event.code,
            data: null,
            error: event,
          });
        };
      } else {
        if (!jwtToken) {
          reject({
            status: 401,
            data: null,
            error:
              "Your session has expired. If you're not automatically redirected, please navigate to the home page.",
          });
        }

        if (!initialRender && !accounts_details) {
          reject({
            status: 400,
            data: null,
            error:
              "Please make sure you have active portfolios, accounts, and venue accounts before proceeding.",
          });
        }
      }
    });
  };

  const handleNotionalAssetChange = async (notional_asset_value: string) => {
    let accounts: string | null = null;

    setLoading(true);

    if (context_state.selected_account?.account_id === ALL_ACCOUNTS) {
      setColDefs(
        getColDefs(
          view_param === LIST
            ? colDefKeys.all_accounts_list
            : colDefKeys.all_accounts_notional
        )
      );

      accounts =
        context_state &&
        context_state.selected_portfolio &&
        context_state.selected_portfolio.accounts &&
        context_state.selected_portfolio.accounts
          .map((account: any) => account.account_id)
          .join(",");
    } else {
      setColDefs(
        getColDefs(view_param === LIST ? colDefKeys.list : colDefKeys.notional)
      );

      accounts = context_state.selected_account
        ? context_state.selected_account.account_id
        : null;
    }

    try {
      await handleNotionalSocketConnections(
        accounts || "",
        context_state.selected_portfolio?.accounts || null,
        notional_asset_value
      );
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.message,
        status: error_res?.status || 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangeListNetNotionalViews = async (
    listNotionalView: string,
    value: any
  ) => {
    if (alerts.length > 0) clearAlerts();
    if (!value) return;

    setLoading(true);
    const accounts =
      accounts_param === ALL_ACCOUNTS
        ? context_state &&
          context_state.selected_portfolio &&
          context_state.selected_portfolio.accounts &&
          context_state.selected_portfolio.accounts
            .map((account: any) => account.account_id)
            .join(",")
        : context_state.selected_account &&
          context_state.selected_account.account_id;

    disconnectFromWebsocket();
    setDataMapRef(new Map([]));

    try {
      if (accounts) {
        if (value === LIST && listNotionalView !== LIST) {
          setColDefs(
            getColDefs(
              accounts_param === ALL_ACCOUNTS
                ? colDefKeys.all_accounts_list
                : colDefKeys.list
            )
          );

          await handleSocketConnections(
            accounts,
            context_state.selected_portfolio?.accounts || null
          );
        }

        if (value === NOTIONAL && listNotionalView !== NOTIONAL) {
          setColDefs(
            getColDefs(
              accounts_param === ALL_ACCOUNTS
                ? colDefKeys.all_accounts_notional
                : colDefKeys.notional
            )
          );

          await handleNotionalSocketConnections(
            accounts,
            context_state.selected_portfolio?.accounts || null,
            notional_asset_param || "USDT"
          );
        }
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.message,
        status: error_res?.status || 500,
      });
    } finally {
      setQueryParams((prev: URLSearchParams) => {
        prev.set("view", value);
        return prev;
      });
      setLoading(false);
    }
  };

  const handleSocketConnections = async (
    accounts: string,
    accounts_details: IAccountMetadata[] | null
  ) => {
    setTableLoading(true);

    try {
      await connectToWebsocket(
        accounts,
        accounts_details,
        `${get_query_endpoint("list")}${accounts}`
      );
    } catch (error_res: any) {
      if (error_res.status === 401 || error_res.status === 408) {
        pushAlert({
          id: uuidv4(),
          message: error_res.message,
          status: error_res.status,
          onClickText: "Reload page?",
          onClickMethod: () => window.location.reload(),
        });
      } else {
        pushAlert({
          id: uuidv4(),
          message: error_res.message,
          status: error_res?.status || 500,
        });
      }
    } finally {
      setTableLoading(false);
      if (initialRender) setInitialRender(false);
    }
  };

  const handleNotionalSocketConnections = async (
    accounts: string,
    accounts_details: IAccountMetadata[] | null,
    notional_asset: string
  ) => {
    setTableLoading(true);

    try {
      await connectToWebsocket(
        accounts,
        accounts_details,
        `${get_query_endpoint(
          "notional"
        )}${accounts}&notional_asset=${notional_asset?.toUpperCase()}`
      );
    } catch (error_res: any) {
      if (error_res.status === 401) {
        pushAlert({
          id: uuidv4(),
          message: error_res.message,
          status: 401,
        });
      } else if (error_res.status === 408) {
        pushAlert({
          id: uuidv4(),
          message: error_res.message,
          status: 408,
          onClickText: "Reload page?",
          onClickMethod: () => window.location.reload(),
        });
      } else {
        pushAlert({
          id: uuidv4(),
          message: error_res.message,
          status: error_res?.status || 500,
        });
      }
    } finally {
      setTableLoading(false);
      if (initialRender) setInitialRender(false);
    }
  };

  const getColDefs = (colDefKeys: string[]): ColDef[] => {
    return colDefKeys
      .map((key) => initialColDefs.get(key))
      .filter((colDef) => colDef !== undefined) as ColDef[];
  };
  // Methods - END -------------------------------------

  // Table Configurations - START ----------------------
  const colDefKeys: {
    list: string[];
    notional: string[];
    all_accounts_list: string[];
    all_accounts_notional: string[];
  } = {
    list: [
      "symbol",
      "rpt_id",
      "venue",
      "long_qty",
      "short_qty",
      "list_unrealized_pnl",
      "clearing_date",
      "updated",
      "wallet",
    ],
    notional: [
      "symbol",
      "rpt_id",
      "venue",
      "notional_asset",
      "notional_long",
      "notional_short",
      "notional_unrealized_pnl",
      "clearing_date",
      "updated",
      "wallet",
    ],
    all_accounts_list: [
      "symbol",
      "rpt_id",
      "account",
      "venue",
      "long_qty",
      "short_qty",
      "list_unrealized_pnl",
      "clearing_date",
      "updated",
      "wallet",
    ],
    all_accounts_notional: [
      "symbol",
      "rpt_id",
      "account",
      "venue",
      "notional_asset",
      "notional_long",
      "notional_short",
      "notional_unrealized_pnl",
      "clearing_date",
      "updated",
      "wallet",
    ],
  };

  const initialColDefs: Map<
    string,
    ColDef<IAGPositionsSocketDataRow>
  > = new Map([
    [
      "symbol",
      {
        headerName: "Symbol",
        field: "symbol",
        cellClass: "ag-center-aligned-cell",
      },
    ],
    [
      "rpt_id",
      {
        headerName: "Report ID",
        field: "rpt_id",
        cellClass: "ag-center-aligned-cell",
      },
    ],
    [
      "account",
      {
        headerName: "Account",
        field: "account",
        cellClass: "ag-center-aligned-cell",
      },
    ],
    [
      "venue",
      {
        headerName: "Venue",
        field: "venue",
        cellClass: "ag-center-aligned-cell",
        cellRenderer: VenueCell,
      },
    ],
    [
      "long_qty",
      {
        headerName: "Long",
        field: "long_qty",
        cellClass: "ag-center-aligned-cell",
        valueFormatter: numericFormatterNoPrecision,
        comparator: customNumericSortFunc,
      },
    ],
    [
      "short_qty",
      {
        headerName: "Short",
        field: "short_qty",
        cellClass: "ag-center-aligned-cell",
        valueFormatter: numericFormatterNoPrecision,
        comparator: customNumericSortFunc,
      },
    ],
    [
      "notional_asset",
      {
        headerName: "Notional Asset",
        field: "notional_asset",
        cellClass: "ag-center-aligned-cell",
      },
    ],
    [
      "notional_long",
      {
        headerName: "$Long",
        field: "notional_long",
        cellClass: "ag-center-aligned-cell",
        valueFormatter: (params: any) => currencyFormatter(params, ""),
        comparator: customNumericSortFunc,
      },
    ],
    [
      "notional_short",
      {
        headerName: "$Short",
        field: "notional_short",
        cellClass: "ag-center-aligned-cell",
        valueFormatter: (params: any) => currencyFormatter(params, ""),
        comparator: customNumericSortFunc,
      },
    ],
    [
      "list_unrealized_pnl",
      {
        headerName: "$Unrealized P&L",
        field: "list_unrealized_pnl",
        cellClass: "ag-center-aligned-cell",
        valueFormatter: (params: any) => currencyFormatter(params, ""),
        comparator: customNumericSortFunc,
      },
    ],
    [
      "notional_unrealized_pnl",
      {
        headerName: "$Unrealized P&L",
        field: "notional_unrealized_pnl",
        cellClass: "ag-center-aligned-cell",
        valueFormatter: (params: any) => currencyFormatter(params, ""),
        comparator: customNumericSortFunc,
      },
    ],
    [
      "clearing_date",
      {
        headerName: "Date",
        field: "clearing_date",
        cellClass: "ag-center-aligned-cell",
      },
    ],
    [
      "updated",
      {
        headerName: "Updated",
        field: "updated",
        cellClass: "ag-center-aligned-cell",
      },
    ],
    [
      "wallet",
      {
        headerName: "Wallet",
        field: "wallet",
        cellClass: "ag-center-aligned-cell",
      },
    ],
  ]);

  const [colDefs, setColDefs] = useState<ColDef<IAGPositionsSocketDataRow>[]>();

  const defaultColDef: ColDef = {
    filter: true,
  };
  // Table Configurations - END ------------------------

  // useEffects - START --------------------------------
  window.addEventListener("beforeunload", () => {
    disconnectFromWebsocket();
  });

  useEffect(() => {
    handleInitialDataLoad();

    return () => {
      disconnectFromWebsocket();
    };
  }, []);

  useEffect(() => {
    if (readyForSocket && context_state.selected_account?.account_id) {
      let updated_accounts: string = context_state.selected_account.account_id;

      if (context_state.selected_account.account_id === ALL_ACCOUNTS) {
        updated_accounts =
          (context_state.selected_portfolio &&
            context_state.selected_portfolio.accounts &&
            context_state.selected_portfolio.accounts
              .map((account: any) => account.account_id)
              .join(",")) ||
          "";
      }

      if (view_param === LIST) {
        handleSocketConnections(
          updated_accounts,
          context_state.selected_portfolio?.accounts || null
        );
      } else {
        handleNotionalSocketConnections(
          updated_accounts,
          context_state.selected_portfolio?.accounts || null,
          notional_asset_param || "USDT"
        );
      }
    }
  }, [readyForSocket]);

  useEffect(() => {
    if (!initialRender) {
      setDataMapRef(new Map([]));

      if (context_state.selected_account) {
        handleNavigate(
          context_state.selected_account.account_id,
          view_param || LIST,
          (view_param === NOTIONAL && notional_asset_param) || "USDT"
        );

        let accounts: string | null = null;

        if (context_state.selected_account?.account_id === ALL_ACCOUNTS) {
          setColDefs(
            getColDefs(
              view_param === LIST
                ? colDefKeys.all_accounts_list
                : colDefKeys.all_accounts_notional
            )
          );

          accounts = accounts =
            context_state.selected_portfolio &&
            context_state.selected_portfolio.accounts &&
            context_state.selected_portfolio.accounts
              .map((account: any) => account.account_id)
              .join(",");
        } else {
          setColDefs(
            getColDefs(
              view_param === LIST ? colDefKeys.list : colDefKeys.notional
            )
          );

          accounts = context_state.selected_account.account_id;
        }

        if (accounts) {
          if (view_param === LIST) {
            handleSocketConnections(
              accounts,
              context_state.selected_portfolio?.accounts || null
            );
          }

          if (view_param === NOTIONAL) {
            handleNotionalSocketConnections(
              accounts,
              context_state.selected_portfolio?.accounts || null,
              notional_asset_param || "USDT"
            );
          }
        }
      } else {
        setColDefs(getColDefs(colDefKeys.all_accounts_list));
        handleNavigate("", LIST, null);
      }
    }
  }, [context_state.selected_account]);

  useEffect(() => {
    if (view_param === LIST || view_param === NOTIONAL) {
      setQueryParams((prev: URLSearchParams) => {
        prev.set("view", view_param);
        return prev;
      });

      setColDefs(
        getColDefs(view_param === LIST ? colDefKeys.list : colDefKeys.notional)
      );
    } else {
      setQueryParams((prev: URLSearchParams) => {
        prev.set("view", LIST);
        return prev;
      });

      setColDefs(getColDefs(colDefKeys.list));
    }
  }, [view_param]);
  // useEffects - END ----------------------------------

  return (
    <div className="pulse-sidenav-adjusted-container pulse-bg-color--background-900">
      <SvgIcon
        sx={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
        children={<LogoSVG className="pulse-color--background-800" />}
      />

      {alerts.map((alert: IAlertMessage) => {
        return (
          <div className="pulse-mb-1" style={{ width: "100%" }} key={uuidv4()}>
            <AlertMessage
              id={alert.id}
              message={alert.message}
              status={alert.status}
              closeAlertMessage={closeAlert}
            />
          </div>
        );
      })}

      <div className="pulse-row positions-header-row positions-position-relative">
        <p className="pulse-typography-headline-6 pulse-color--background-50">
          Positions
        </p>
        <div className="positions-dropdown-inputs-container">
          <DropdownInput
            name="portfolio-select"
            value={context_state.selected_portfolio?.portfolio_id || ""}
            label="Portfolio"
            onChange={handleDropdownChange}
            disabled={context_state.org_metadata.portfolios.length < 1}
            options={mapIPortfoliosToSelectOptions(
              context_state.org_metadata.portfolios
            )}
            formInput={false}
            containerClassNames=""
          />

          <DropdownInput
            name="account-select"
            value={context_state.selected_account?.account_id || ""}
            label="Account"
            onChange={handleDropdownChange}
            disabled={
              context_state.selected_portfolio?.accounts
                ? context_state.selected_portfolio?.accounts.length < 1
                  ? true
                  : false
                : true
            }
            options={mapIAccountsToSelectOptions(
              context_state.selected_portfolio?.accounts || []
            )}
            formInput={false}
            containerClassNames=""
          />

          {view_param === NOTIONAL && (
            <DropdownInput
              name="notional-asset-select"
              value={notional_asset_param || "USDT"}
              label="Notional Asset"
              onChange={handleDropdownChange}
              disabled={!view_param || view_param.length < 1}
              options={pricingAssets}
              formInput={false}
              containerClassNames="last-child-margin-left"
              omitEmptyOption
            />
          )}
        </div>
      </div>

      <div className="pulse-row pulse-mt-1 pulse-mb-2 pulse-justify-space-between positions-position-relative positions-table-header-container">
        <div className="pulse-col-md-8 pulse-col-lg-8 pulse-col-xl-8 positions-table-header-left">
          {context_state.selected_account &&
            context_state.selected_account.account_id && (
              <ToggleButtonGroup
                value={view_param}
                exclusive
                onChange={(
                  _: React.MouseEvent<HTMLElement, MouseEvent>,
                  value: any
                ) =>
                  handleChangeListNetNotionalViews(view_param || LIST, value)
                }
                aria-label="text alignment"
                className="positions-toggle-button-group pulse-mr-1"
              >
                <ToggleButton
                  value={LIST}
                  aria-label={LIST}
                  className={`positions-toggle-button ${
                    view_param === LIST && "selected"
                  }`}
                >
                  List
                </ToggleButton>
                <ToggleButton
                  value={NOTIONAL}
                  aria-label={NOTIONAL}
                  className={`positions-toggle-button ${
                    view_param === NOTIONAL && "selected"
                  }`}
                >
                  Notional
                </ToggleButton>
              </ToggleButtonGroup>
            )}
        </div>
      </div>

      <SocketAGTable
        gridRef={gridRef}
        rowData={[...dataMapRef.current.values()]}
        colDefs={colDefs}
        defaultColDef={defaultColDef}
        loading={tableLoading}
      />

      {loading && <Loading />}
    </div>
  );
};

export default Positions;
